import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthStore } from '@/stores/authStore';
import { fetchStaffComments, createStaffComment, type StaffComment } from '@/services/data/staffComments';
import { MentionInput } from '@/components/chat/MentionInput';

interface StaffChatThreadProps {
  videoContentId: string;
}

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `${diffMin}min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

// Render text with @mentions highlighted
function renderCommentText(text: string): React.ReactNode {
  const mentionRegex = /@([\p{L}\p{M}][\p{L}\p{M}\s'-]*[\p{L}\p{M}])/gu;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <span key={match.index} className="text-warning font-medium">
        @{match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

export function StaffChatThread({ videoContentId }: Readonly<StaffChatThreadProps>) {
  const { userId } = useAuthStore();
  const [comments, setComments] = useState<StaffComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchStaffComments(videoContentId).then(res => {
      if (res.success && res.data) setComments(res.data);
      setLoading(false);
    });
  }, [videoContentId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments]);

  const handleSend = async () => {
    if (!text.trim() || !userId || sending) return;
    setSending(true);
    const res = await createStaffComment({
      videoContentId,
      userId,
      text: text.trim(),
    });
    if (res.success && res.data) {
      setComments(prev => [...prev, res.data]);
      setText('');
    }
    setSending(false);
  };

  const hasComments = comments.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-3 mb-3 min-h-0"
      >
        {loading && (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-white/30" />
          </div>
        )}
        {!loading && !hasComments && (
          <p className="text-xs text-white/30 text-center py-3">
            Aucun message pour le moment
          </p>
        )}
        {!loading && hasComments && (
          comments.map(c => {
            const isOwn = c.user_id === userId;
            const initials = `${c.author_first_name?.[0] || ''}${c.author_last_name?.[0] || ''}`.toUpperCase() || '?';
            const name = [c.author_first_name, c.author_last_name].filter(Boolean).join(' ') || 'Staff';

            return (
              <div key={c.id} className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
                <Avatar className="h-6 w-6 flex-shrink-0">
                  <AvatarImage src={c.author_avatar_url || undefined} />
                  <AvatarFallback className="text-[9px] bg-white/10">{initials}</AvatarFallback>
                </Avatar>
                <div className={`max-w-[75%] ${isOwn ? 'text-right' : ''}`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {!isOwn && <span className="text-[10px] text-white/50 font-medium">{name}</span>}
                    <span className="text-[9px] text-white/30">{formatTimeAgo(c.created_at)}</span>
                  </div>
                  <div className={`inline-block px-3 py-1.5 rounded-xl text-xs text-white/90 leading-relaxed ${
                    isOwn ? 'bg-warning/20 rounded-tr-sm' : 'bg-white/8 rounded-tl-sm'
                  }`}>
                    {renderCommentText(c.comment_text)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input with @mentions */}
      <div className="flex gap-2 items-center">
        <MentionInput
          value={text}
          onChange={(value) => {
            setText(value);
          }}
          placeholder="Message interne"
          disabled={sending}
          onSubmit={handleSend}
          className="h-9 text-xs !bg-white/5 !border-white/10 !text-white placeholder:!text-white/30 focus-visible:!ring-0 focus-visible:!ring-offset-0 focus-visible:!border-white/20"
        />
        <button
          onClick={handleSend}
          disabled={!text.trim() || sending}
          className="flex-shrink-0 p-2 rounded-lg bg-warning/10 border border-warning/30 text-warning hover:bg-warning/20 transition-all disabled:opacity-30 disabled:cursor-default"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}
